import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { setSeo, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const HERO = "https://images.unsplash.com/photo-1772474557170-4818d01d7bca?crop=entropy&cs=srgb&fm=jpg&q=90&w=900";

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => setSeo("Login / Register | Adam's The Islamic Boutique", "Sign in to your Adam's account."), []);
  useEffect(() => { if (user) navigate("/account"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = mode === "login" ? await login(form.email, form.password) : await register(form.name, form.email, form.password);
      toast.success(mode === "login" ? "Welcome back" : "Welcome to the Adam's family");
      navigate(u.role === "admin" ? "/admin" : "/account");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src={HERO} alt="Adam's modest fashion" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-espresso/30" />
        <div className="absolute bottom-10 left-10 text-ivory">
          <p className="font-accent text-xs tracking-[0.3em] uppercase text-champagne-light">The Islamic Boutique</p>
          <p className="font-serif text-4xl mt-2">Modesty,<br />Redefined.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <h1 className="font-serif text-4xl text-espresso" data-testid="auth-title">{mode === "login" ? "Welcome Back" : "Create Account"}</h1>
          <p className="text-espresso/50 text-sm mt-2">{mode === "login" ? "Sign in to continue shopping." : "Join the Adam's family today."}</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full Name" className="w-full border border-espresso/20 rounded-xl px-4 py-3.5 bg-ivory outline-none focus:border-champagne text-sm" data-testid="auth-name" />
            )}
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email Address" className="w-full border border-espresso/20 rounded-xl px-4 py-3.5 bg-ivory outline-none focus:border-champagne text-sm" data-testid="auth-email" />
            <input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password" className="w-full border border-espresso/20 rounded-xl px-4 py-3.5 bg-ivory outline-none focus:border-champagne text-sm" data-testid="auth-password" />
            {error && <p className="text-sm text-[#a13b2f]" data-testid="auth-error">{error}</p>}
            <button type="submit" disabled={loading} className="w-full bg-espresso text-ivory py-3.5 rounded-full text-sm font-medium hover:bg-espresso/90 flex items-center justify-center gap-2 disabled:opacity-60" data-testid="auth-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>

          <p className="text-center text-sm text-espresso/60 mt-6">
            {mode === "login" ? "New to Adam's?" : "Already have an account?"}{" "}
            <button onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }} className="text-champagne font-medium gold-underline" data-testid="auth-toggle">
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>

          <div className="relative my-7">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-champagne/25" /></div>
            <div className="relative flex justify-center"><span className="bg-ivory px-4 text-xs uppercase tracking-widest text-espresso/40">or</span></div>
          </div>

          <button onClick={() => navigate("/shop")} className="w-full border border-espresso/25 py-3.5 rounded-full text-sm font-medium text-espresso hover:border-champagne hover:bg-beige/40 transition-colors" data-testid="continue-guest">
            Continue as Guest
          </button>
          <p className="text-center text-xs text-espresso/40 mt-3">Shop & checkout without an account — no sign-up needed.</p>
        </div>
      </div>
    </div>
  );
}
